import { useEffect, useMemo, useState } from "react";
import GridLoader from "react-spinners/GridLoader";
import { FiRefreshCw } from "react-icons/fi";
import type { PaginationState } from "@tanstack/react-table";
import type { Vendor } from "../../../../features/interfaces";
import { useLazyGetVendorRemoteProductsQuery } from "../../../../features/vendors/vendorApi";
import SlideInModal from "../../../../components/modals/SlideInModal";
import Table from "../../../../components/list/Table";
import Pagination from "../../../../components/list/Pagination";
import { toast } from "react-toastify";
import {
  collectRemoteProductStatuses,
  filterRemoteProducts,
} from "./filterRemoteProducts";
import { getRemoteProductColumns } from "./remoteProductColumns";
import { isVendorIntegrationActive } from "./hasVendorIntegration";

type VendorRemoteProductsModalProps = {
  vendor: Vendor | null;
  isOpen: boolean;
  onClose: () => void;
};

/**
 * Presentation + orchestration for one vendor's remote catalog.
 * Depends on RTK Query abstraction (DIP); filtering/columns are separate modules (SRP).
 */
const VendorRemoteProductsModal = ({
  vendor,
  isOpen,
  onClose,
}: VendorRemoteProductsModalProps) => {
  const [nameFilter, setNameFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 50,
  });

  const [
    fetchRemoteProducts,
    {
      data: productsResponse,
      isFetching,
      isLoading,
      error,
      reset,
    },
  ] = useLazyGetVendorRemoteProductsQuery();

  const productColumns = useMemo(() => getRemoteProductColumns(), []);
  const remoteProducts = productsResponse?.data?.products || [];

  const statusOptions = useMemo(
    () => collectRemoteProductStatuses(remoteProducts),
    [remoteProducts]
  );

  const filteredProducts = useMemo(
    () =>
      filterRemoteProducts(remoteProducts, {
        name: nameFilter,
        status: statusFilter,
      }),
    [remoteProducts, nameFilter, statusFilter]
  );

  const loadProducts = async (target: Vendor) => {
    if (!target.vendor_id) return;
    if (!isVendorIntegrationActive(target)) {
      toast.error("This vendor's integration is not active");
      return;
    }
    try {
      await fetchRemoteProducts(target.vendor_id).unwrap();
    } catch (err: any) {
      toast.error(
        err?.data?.message || "Failed to load vendor products from integration"
      );
    }
  };

  useEffect(() => {
    if (!isOpen || !vendor) {
      setNameFilter("");
      setStatusFilter("");
      setPagination({ pageIndex: 0, pageSize: 50 });
      reset();
      return;
    }
    setNameFilter("");
    setStatusFilter("");
    setPagination({ pageIndex: 0, pageSize: 50 });
    void loadProducts(vendor);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, vendor?.vendor_id]);

  const errorMessage =
    error &&
    ((error as any)?.data?.message ||
      "Could not load products for this vendor");

  return (
    <SlideInModal
      isOpen={isOpen}
      onClose={onClose}
      title={vendor ? `${vendor.name} products` : "Vendor products"}
      subtitle={
        vendor?.integration_type
          ? `Fetched via ${vendor.integration_type}`
          : undefined
      }
      width="100vw"
    >
      <div className="flex justify-end mb-3">
        <button
          type="button"
          onClick={() => vendor && void loadProducts(vendor)}
          disabled={!vendor || isFetching}
          className="px-3 py-2 text-sm font-semibold text-white rounded-lg flex items-center gap-2 disabled:opacity-50"
          style={{ backgroundColor: "#dbb685" }}
        >
          <FiRefreshCw size={14} />
          Refresh
        </button>
      </div>

      {isLoading || isFetching ? (
        <div className="flex justify-center items-center h-64">
          <GridLoader color="#dbb685" />
        </div>
      ) : errorMessage ? (
        <div className="flex flex-col items-center justify-center h-64 text-center px-4">
          <p className="text-sm font-semibold text-rose-600">{errorMessage}</p>
          <p className="text-xs text-gray-500 mt-2">
            Check shop_url and api_token on the vendor&apos;s integration
            credentials.
          </p>
        </div>
      ) : (
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-100 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <p className="text-sm text-gray-600">
              Showing{" "}
              <span className="font-semibold text-gray-800">
                {filteredProducts.length}
              </span>{" "}
              of{" "}
              <span className="font-semibold text-gray-800">
                {remoteProducts.length}
              </span>{" "}
              products
            </p>
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
              <input
                type="text"
                value={nameFilter}
                onChange={(e) => {
                  setNameFilter(e.target.value);
                  setPagination((prev) => ({ ...prev, pageIndex: 0 }));
                }}
                placeholder="Filter by product name"
                className="py-2 px-3 border border-gray-200 rounded-lg text-sm text-gray-700 bg-white focus:outline-none focus:ring-2 focus:ring-[#dbb685] focus:border-[#dbb685] min-w-[200px]"
              />
              <select
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  setPagination((prev) => ({ ...prev, pageIndex: 0 }));
                }}
                className="py-2 px-3 border border-gray-200 rounded-lg text-sm text-gray-700 bg-white focus:outline-none focus:ring-2 focus:ring-[#dbb685] focus:border-[#dbb685] min-w-[150px]"
              >
                <option value="">All statuses</option>
                {statusOptions.map((status) => (
                  <option key={status} value={status}>
                    {status.charAt(0).toUpperCase() + status.slice(1)}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <Table
            columns={productColumns}
            data={filteredProducts}
            isSearchable={false}
            isSortable
            pagination
            paginationState={pagination}
            setPaginationState={setPagination}
            tableClass="w-full"
            thClass="text-gray-500 font-semibold text-xs uppercase tracking-wide py-2.5 px-3 whitespace-nowrap"
            tdClass="text-sm py-2 px-3 text-gray-700"
            theadRowClass="bg-gray-50 border-b border-gray-200"
          />

          <div className="flex justify-between items-center p-3 border-t border-gray-200">
            <span className="text-sm text-gray-600">
              Page {pagination.pageIndex + 1}
            </span>
            <Pagination
              currentPage={pagination.pageIndex + 1}
              totalItems={filteredProducts.length}
              itemsPerPage={pagination.pageSize}
              onPageChange={(p) =>
                setPagination({ ...pagination, pageIndex: p - 1 })
              }
              btnClass="px-3 py-1.5 border border-gray-300 text-sm rounded-lg hover:bg-gray-50"
              pageNumClass="text-sm px-2 py-1"
              activePageNumClass="text-white rounded-lg font-semibold"
              activePageNumStyle={{ backgroundColor: "#dbb685" }}
              inactivePageNumClass="hover:bg-gray-100 rounded-lg"
            />
          </div>
        </div>
      )}
    </SlideInModal>
  );
};

export default VendorRemoteProductsModal;
